import { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Switch from '@/components/Switch'
import { setDataTable } from '@/actions'
import Icon from '@/components/Icon'
import Button from '@/components/Button'
import SharedModal from '@/components/Modal/SharedModal'
import { useAnalytics } from '@/contexts/AnalyticsProvider'
import ApplyFilterToMap from './ApplyFilterToMap'
import scss from './DataTable.scss'

/**
 * SaveCancelButtons - Reusable component for save/cancel button group
 * Memoized to prevent unnecessary re-renders
 */
const SaveCancelButtons = memo(({ isSaving, onSave, onCancel }) => {
  const cancelIconProps = useMemo(() => ({ icon: 'times' }), [])
  const saveIconProps = useMemo(() => ({ icon: 'save' }), [])

  return (
    <div className={scss.saveButtonGroup}>
      <Button
        onClick={onCancel}
        disabled={isSaving}
        variant='tertiary'
        size='sm'
        iconProps={cancelIconProps}
        label='Cancel'
        data-testid='data-table-cancel-button'
      />
      <Button
        onClick={onSave}
        disabled={isSaving}
        variant='primary'
        size='sm'
        loading={isSaving}
        iconProps={saveIconProps}
        label={isSaving ? 'Saving...' : 'Save'}
        data-testid='data-table-save-button'
      />
    </div>
  )
})

SaveCancelButtons.displayName = 'SaveCancelButtons'

/**
 * CloseButton - Reusable component for data table close button
 * Memoized to prevent unnecessary re-renders
 */
const CloseButton = memo(({ onClose, loading }) => {
  const closeIconProps = useMemo(
    () => ({
      icon: 'times',
      size: 'lg',
    }),
    []
  )

  return (
    <Button
      variant='tertiary'
      size='sm'
      onClick={onClose}
      loading={loading}
      iconProps={closeIconProps}
      aria-label='Close data table'
      data-testid={`data-table-close-button${loading ? '-spinner' : ''}`}
    />
  )
})

CloseButton.displayName = 'CloseButton'

const TableControls = ({
  fetching,
  gridApi,
  currentFilter,
  onClose,
  rowCount,
  hasUnsavedChanges = false,
  onSaveChanges,
  onCancelChanges,
  isSaving = false,
  onExport,
  exporting = false,
  tabsEnabled = false,
  onToggleTabs,
}) => {
  const dispatch = useDispatch()
  const dataTable = useSelector(state => state.dataTable)
  const { trackEvent } = useAnalytics()
  const initialFilterState = Boolean(currentFilter)
  const [doApplyFilter, setDoApplyFilter] = useState(initialFilterState)
  const [filterChecked, setFilterChecked] = useState(initialFilterState)
  const [closingTable, setClosingTable] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [closeAfterSave, setCloseAfterSave] = useState(false)
  const [hasActiveGridFilters, setHasActiveGridFilters] = useState(false)
  const layerLabelFromDataTable = dataTable?.layerLabel || ''

  const closeTable = useCallback(() => {
    setClosingTable(true)

    // Track analytics event for data table close
    trackEvent('data_table_closed', {
      tool_name: 'data_table',
      layer_label: layerLabelFromDataTable,
      row_count: rowCount,
      has_unsaved_changes: hasUnsavedChanges,
    })
  }, [trackEvent, layerLabelFromDataTable, rowCount, hasUnsavedChanges])

  const handleCloseAttempt = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true)
    } else {
      closeTable()
    }
  }, [hasUnsavedChanges, closeTable])

  const handleModalSave = useCallback(() => {
    trackEvent('data_table_close_confirmed', {
      tool_name: 'data_table',
      action: 'save',
      layer_label: layerLabelFromDataTable,
    })
    setShowUnsavedModal(false)
    if (onSaveChanges) {
      setCloseAfterSave(true)
      onSaveChanges()
    }
  }, [trackEvent, layerLabelFromDataTable, onSaveChanges])

  const handleModalDiscard = useCallback(() => {
    trackEvent('data_table_close_confirmed', {
      tool_name: 'data_table',
      action: 'discard',
      layer_label: layerLabelFromDataTable,
    })
    setShowUnsavedModal(false)
    if (onCancelChanges) {
      onCancelChanges()
    }
    closeTable()
  }, [trackEvent, layerLabelFromDataTable, onCancelChanges, closeTable])

  const handleModalCancel = useCallback(() => {
    trackEvent('data_table_close_cancelled', {
      tool_name: 'data_table',
      layer_label: layerLabelFromDataTable,
    })
    setShowUnsavedModal(false)
  }, [trackEvent, layerLabelFromDataTable])

  const applyFilter = useCallback(() => {
    const newState = !filterChecked
    setDoApplyFilter(newState)
    setFilterChecked(newState)

    // Track analytics event for map filter toggle
    trackEvent('data_table_map_filter_toggled', {
      tool_name: 'data_table',
      filter_state: newState ? 'enabled' : 'disabled',
      layer_label: layerLabelFromDataTable,
    })
  }, [filterChecked, trackEvent, layerLabelFromDataTable])

  const resetFilters = useCallback(() => {
    if (!gridApi) return

    // Clear all filters using AG Grid's built-in API
    gridApi.setFilterModel(null)

    // Immediately update UI state
    setHasActiveGridFilters(false)

    // Track analytics event for filter clearing
    trackEvent('data_table_filters_cleared', {
      tool_name: 'data_table',
      layer_label: layerLabelFromDataTable,
    })
  }, [gridApi, trackEvent, layerLabelFromDataTable])

  const exportToExcel = useCallback(() => {
    if (exporting || !onExport) return
    onExport()
  }, [exporting, onExport])

  const handleSaveChanges = useCallback(() => {
    if (onSaveChanges) {
      onSaveChanges()
    }
  }, [onSaveChanges])

  const handleCancelChanges = useCallback(() => {
    if (onCancelChanges) {
      onCancelChanges()
    }
  }, [onCancelChanges])

  // Removed - initialization now handled in useState above

  // Track grid filter changes to enable/disable Reset Filters button
  useEffect(() => {
    if (!gridApi) return
    const update = () => {
      const model = gridApi.getFilterModel?.() || {}
      setHasActiveGridFilters(Object.keys(model).length > 0)
    }
    update()
    gridApi.addEventListener('filterChanged', update)
    return () => {
      gridApi.removeEventListener('filterChanged', update)
    }
  }, [gridApi])

  // Close table after save completes successfully
  useEffect(() => {
    if (closeAfterSave && !isSaving && !hasUnsavedChanges) {
      setCloseAfterSave(false)
      closeTable()
    }
  }, [closeAfterSave, isSaving, hasUnsavedChanges, closeTable])

  useEffect(() => {
    if (!closingTable) return

    const timeoutId = setTimeout(() => {
      dispatch(setDataTable(null))
      onClose()
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [closingTable, dispatch, onClose])

  return (
    <>
      {gridApi ? (
        <ApplyFilterToMap
          doApplyFilter={doApplyFilter}
          currentFilter={currentFilter}
          gridApi={gridApi}
        >
          <div className={scss.headerContainer}>
            <div className={scss.tableLabel}>
              <div>
                {layerLabelFromDataTable}
                {fetching ? (
                  <span>
                    {rowCount ? (
                      `${rowCount} result${rowCount === 1 ? '' : 's'}`
                    ) : (
                      <Icon icon='spinner' size='1x' spin />
                    )}
                  </span>
                ) : (
                  <span>
                    {rowCount
                      ? `${rowCount} result${rowCount === 1 ? '' : 's'}`
                      : null}
                  </span>
                )}
              </div>
            </div>
            <div className={scss.buttonWrapper}>
              {hasUnsavedChanges && (
                <SaveCancelButtons
                  isSaving={isSaving}
                  onSave={handleSaveChanges}
                  onCancel={handleCancelChanges}
                />
              )}

              <Button
                onClick={exportToExcel}
                variant='tertiary'
                size='sm'
                iconProps={{ icon: 'download' }}
                label={exporting ? 'Exporting...' : 'Export'}
                loading={exporting}
                disabled={!gridApi || exporting || !onExport}
                data-testid='data-table-export-button'
              />
              {onToggleTabs && (
                <Button
                  onClick={onToggleTabs}
                  variant='tertiary'
                  size='sm'
                  active={tabsEnabled}
                  iconProps={{ icon: 'layer-group' }}
                  label='Tabs'
                  aria-pressed={tabsEnabled}
                  aria-label={
                    tabsEnabled
                      ? 'Disable popup tab grouping in data table'
                      : 'Enable popup tab grouping in data table'
                  }
                  data-testid='data-table-tabs-toggle-button'
                />
              )}
              <Button
                onClick={resetFilters}
                variant='tertiary'
                size='sm'
                iconProps={{ icon: 'times-circle' }}
                label='Reset Filters'
                data-testid='data-table-reset-filters-button'
                disabled={!hasActiveGridFilters}
              />
              <div
                className='flex items-center gap-2 px-2 py-1 text-slate-800'
                data-testid='data-table-map-filters-button'
              >
                <span className={scss.buttonLabel}>Map Filters</span>
                <Switch
                  checked={filterChecked}
                  onChange={applyFilter}
                  aria-label='Toggle map filters'
                />
              </div>
              <CloseButton
                onClose={handleCloseAttempt}
                loading={closingTable}
              />
            </div>
          </div>
        </ApplyFilterToMap>
      ) : (
        <div className={scss.headerContainer}>
          <div className={scss.tableLabel}>
            <div>
              {layerLabelFromDataTable}
              {fetching ? (
                <span>
                  <Icon icon='spinner' size='1x' spin />
                </span>
              ) : null}
            </div>
          </div>
          <div className={scss.buttonWrapper}>
            {hasUnsavedChanges && (
              <SaveCancelButtons
                isSaving={isSaving}
                onSave={handleSaveChanges}
                onCancel={handleCancelChanges}
              />
            )}

            <CloseButton onClose={handleCloseAttempt} loading={closingTable} />
          </div>
        </div>
      )}

      {/* Unsaved Changes Confirmation Modal */}
      <SharedModal
        title='Unsaved Changes'
        isOpen={showUnsavedModal}
        onClose={handleModalCancel}
        size='sm'
        backgroundClickDismiss={false}
        footerActionButtons={[
          {
            label: 'Discard Changes',
            onClick: handleModalDiscard,
            variant: 'destructive',
          },
          {
            label: 'Save and Close',
            onClick: handleModalSave,
            variant: 'primary',
            disabled: isSaving,
            loading: isSaving,
          },
        ]}
        dataTestId='data-table-unsaved-changes-modal'
      >
        <div className='text-white leading-relaxed'>
          You have unsaved changes. Would you like to save them before closing?
        </div>
      </SharedModal>
    </>
  )
}

export default TableControls
